import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import defaultShareroom from "@assets/images/default_shareroom.png";
import { Path } from "@core/routing/path";
import { Group } from "@domain/group/group";
import { categoryIcons } from "@domain/lms/category/category";
import { ImageVersions } from "@domain/media/dtos";
import { useHover, useIsMobile } from "@utils/hooks";
import { theme } from "@ui/styles/theme";
import { alpha, gap, mobile } from "@ui/styles/utils";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { BadgeLabel } from "@ui/components/badgeLabel";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { Clamp } from "@ui/components/containers/clamp";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CommunityIcon } from "@ui/components/icons";
import { Stack } from "@ui/components/layout";
import { MembersButton } from "@ui/components/profile/community/membersButton";
import { DiscussionThumbnailMenu } from "./discussionThumbnailMenu";
import { JoinGroupThumbnailButton } from "./joinGroupThumbnailButton";

interface DiscussionThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	discussion: Group;
	border?: boolean;
}
export const DiscussionThumbnail: React.FC<DiscussionThumbnailProps> = ({ discussion, border, ...htmlProps }) => {
	const { name, description, administrators: teachersList, categories, memberCounts, banner } = discussion;
	const memberStudentsCount = memberCounts.MEMBER ?? 0;
	// const isOpen = discussion.accessMode === "OPEN";
	const { ref, isHovered } = useHover();
	const isMobile = useIsMobile();

	return (
		<Container {...htmlProps} border={border} ref={ref}>
			{/* <Link to={Path.Discussion(discussion)}>

			</Link> */}
			<Content>
				<Link to={Path.Discussion(discussion)}>
					<StyledBackgroundImage
						image={banner}
						defaultUrl={!banner ? defaultShareroom : undefined}
						size={ImageVersions.MEDIUM}
						backdrop
						isHovered={isHovered}
					/>
					<Infos>
						<Stack gap={10}>
							<Title size={3} className="info">
								{name}
							</Title>
							<Description className="info">{description}</Description>
							<MembersCount className="info">
								{I18n.t("group.DISCUSSION_GROUP.thumbnail.members_count", { count: memberStudentsCount })}
							</MembersCount>
						</Stack>
						<Stack gap={13}>
							<TeachersLabel>
								<b>{I18n.t("group.DISCUSSION_GROUP.thumbnail.animated_by")}</b>
							</TeachersLabel>
							<MembersButton sample={teachersList} count={teachersList.length} spacing={-13} imageSize={"M"} />
						</Stack>
						<JoinGroupThumbnailButton group={discussion} />
					</Infos>
					{!isMobile && (
						<Badge
							isHovered={isHovered}
							hoverWidth={250}
							badgeTitle={I18n.t("group.DISCUSSION.thumbnail_badge")}
							renderIcons={() => <CommunityIcon color={theme.colors.secondary} />}
						/>
					)}

					{categories[0] && categories[0] !== "OTHERS" && isMobile && (
						<Category
							category={categories[0]}
							isSmall={isMobile}
							renderIcons={() => <CommunityIcon color={theme.colors.white} />}
						/>
					)}
					{categories[0] && <CategoryImage src={categoryIcons[categories[0] ?? "OTHERS"]} />}
				</Link>
			</Content>
			<StyledMenu discussion={discussion} />
		</Container>
	);
};

const Container = styled(RoundedBlock)<{ border?: boolean }>`
	${({ border }) => !border && "border:none;"}
	position: relative;
`;

const Content = styled(RoundedBlock)`
	max-height: 340px;
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	border-radius: 13px;
	overflow: hidden;

	> a {
		width: 100%;
		height: 100%;
	}

	@media (max-width: 900px) {
		padding-top: 0;
	}
`;

const StyledBackgroundImage = styled(BackgroundImage)`
	height: 342px;
	@media ${mobile} {
		height: 264px;
	}
`;

const Infos = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	padding: 24px;
	color: ${({ theme }) => theme.colors.white};
	// pointer-events: none;
	${gap(15)};
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-end;

	@media ${mobile} {
		padding: 10px;
		${gap(5)};
	}
`;

const Title = styled(Clamp)`
	${({ theme }) => theme.textStyles.subtitle};
	color: ${theme.colors.white};
`;

const Description = styled(Clamp)`
	font-weight: 500;
	color: ${theme.colors.white};
`;

const TeachersLabel = styled.div`
	${({ theme }) => theme.textStyles.primary};
	color: ${theme.colors.white};
	font-weight: 700;
`;

const MembersCount = styled.div`
	${({ theme }) => theme.textStyles.secondary};
	font-weight: 700;
`;

const Category = styled(CategoryLabel)`
	// display: inline-block;
	position: absolute;
	top: 15px;
	// max-width: 70%;
	left: 20px;
`;

const CategoryImage = styled.img`
	position: absolute;
	height: 200px;
	max-width: 100%;
	right: -50px;
	bottom: -50px;
	opacity: 0.1;
	filter: invert(27%) sepia(76%) saturate(2202%) hue-rotate(210deg) brightness(103%) contrast(95%);
`;

const Badge = styled(BadgeLabel)`
	position: absolute;
	top: 0px;
	left: 20px;
`;

const StyledMenu = styled(DiscussionThumbnailMenu)`
	position: absolute;
	right: 24px;
	top: 15px;
	background-color: ${({ theme }) => alpha(theme.colors.backgroundPrimary, 0.98)};
`;
